import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { libraryAPI } from '../api';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

const PENDING_DEEPLINK_KEY = 'pendingDeeplinkAction';

const DeeplinkPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [processing, setProcessing] = useState(true);

  useEffect(() => {
    const handleDeeplink = async () => {
      const type = searchParams.get('type');
      const id = searchParams.get('id');
      const action = searchParams.get('action');
      const digest = searchParams.get('digest');
      const folder = searchParams.get('folder');

      // Store deeplink data for post-login processing
      const deeplinkData = { type, id, action, digest, folder };

      // If not authenticated, store and redirect to login
      if (!authLoading && !user) {
        localStorage.setItem(PENDING_DEEPLINK_KEY, JSON.stringify(deeplinkData));
        toast.info('Please log in to continue');
        navigate('/login', { state: { from: '/deeplink' } });
        return;
      }

      // If still loading auth, wait
      if (authLoading) {
        return;
      }

      // User is authenticated, process the deeplink
      try {
        if (type === 'article' && id) {
          // Handle save action
          if (action === 'save') {
            try {
              await libraryAPI.saveArticle(id);
              toast.success('Article saved to library!');
            } catch (error) {
              if (error.response?.status === 409) {
                toast.info('Article already in your library');
              } else {
                toast.error('Failed to save article');
              }
            }
          }

          // Navigate to article detail page
          navigate(`/article/${id}`, { replace: true });
        } else if (type === 'library' && folder) {
          // Navigate to library with folder filter
          navigate(`/library?folder=${encodeURIComponent(folder)}`, { replace: true });
        } else if (type === 'digest' && digest) {
          // Navigate to digest
          navigate(`/digests/${digest}`, { replace: true });
        } else {
          // Invalid deeplink, go to home
          toast.error('Invalid link');
          navigate('/home', { replace: true });
        }
      } catch (error) {
        console.error('Deeplink processing error:', error);
        toast.error('Something went wrong');
        navigate('/home', { replace: true });
      } finally {
        setProcessing(false);
      }
    };

    handleDeeplink();
  }, [searchParams, user, authLoading, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
        <p className="text-gray-600">Processing your request...</p>
      </div>
    </div>
  );
};

// Helper function to process pending deeplink after login
export const processPendingDeeplink = async (navigate) => {
  const pendingData = localStorage.getItem(PENDING_DEEPLINK_KEY);
  if (!pendingData) return false;

  try {
    const { type, id, action, digest, folder } = JSON.parse(pendingData);
    localStorage.removeItem(PENDING_DEEPLINK_KEY);

    if (type === 'article' && id) {
      if (action === 'save') {
        try {
          await libraryAPI.saveArticle(id);
          toast.success('Article saved to library!');
        } catch (error) {
          if (error.response?.status === 409) {
            toast.info('Article already in your library');
          }
        }
      }
      navigate(`/article/${id}`);
      return true;
    } else if (type === 'library' && folder) {
      navigate(`/library?folder=${encodeURIComponent(folder)}`);
      return true;
    } else if (type === 'digest' && digest) {
      navigate(`/digests/${digest}`);
      return true;
    }
  } catch (error) {
    console.error('Error processing pending deeplink:', error);
    localStorage.removeItem(PENDING_DEEPLINK_KEY);
  }
  return false;
};

export default DeeplinkPage;
